/**
 * 文書とソース → 解決済みタイムライン（索引 Issue #498 の段階 2 → #722）。**純関数**。
 *
 *   生成（generate）→ アンカーの時刻を決める（align。人が動かした時刻は文書の `anchors`）
 *   → 状態チャンネル（states）＋ 描く順に並べた items ＋ 音のきっかけ（cues）
 *
 * ここまでは**データが変わったときだけ**走る。1 フレームごとの評価は `frame.frameAt`。
 */
import { readDocument } from './document';
import { generateObjects } from './generate';
import { DEFAULT_STRATEGY, resolveAnchors } from './align';
import { buildCues } from './cues';
import { EMPTY_REGISTRY, isStateLayer } from './types';
import { buildStates, defineChannel } from './states';

/**
 * 帯の置き場（型の `track`）ごとの重なり。**後ろのものほど手前に描く**（演出がいちばん手前）。
 * 知らない置き場は素材と同じ。
 */
const TRACK_Z = Object.freeze({ video: 0, graphic: 10, se: 20, caption: 30, cut: 40, effect: 50 });
const zOf = (trackKind) => TRACK_Z[trackKind] ?? TRACK_Z.graphic;

/** 描く順（後のものほど手前）。置き場の重なり → 型の登録順 → 開始時刻 */
function compareItems(a, b, order) {
  return (zOf(a.trackKind) - zOf(b.trackKind)) || (order(a.type) - order(b.type)) || (a.startMs - b.startMs);
}

/**
 * @param doc 文書（見るのは `fps` と `anchors`）
 * @param registry 型のレジストリ（`types.createRegistry`）
 * @param anchors ソースのアンカー `[{ key, ms, index, groupId, ordering, payload }]`
 * @param ctx 生成器の `build` に渡すもの（`sourceId` を含む。ドメイン固有の材料は自由）
 * @param strategy 整列戦略（名前か `{ resolve }`）
 * @param states ソースが出す状態チャンネル（LP・ターン・盤面など。`states.js`）
 */
export function buildTimeline({
  doc = null, registry = EMPTY_REGISTRY, anchors = [], ctx = {}, strategy = DEFAULT_STRATEGY, states = [],
} = {}) {
  const d = readDocument(doc);
  const fps = d.fps;

  const generated = generateObjects(anchors, ctx, registry);
  const aligned = resolveAnchors({ anchors, objects: generated, overrides: d.anchors, strategy, fps });

  // **可変尺で出しっぱなしのもの**（`layer: 'state'`）は帯として描かず、状態チャンネルにする（設計文書 §5.5）。
  // 型の宣言から作るチャンネル（帯の集まり）と、ソースが出すチャンネル（畳むもの）を合わせて持つ
  const bandChannels = registry.list().filter((t) => isStateLayer(t.layer))
    .map((t) => defineChannel({ id: t.id, kind: 'bands', label: t.label ?? t.id }, 'types'));
  const channels = [...bandChannels, ...states.map((c, i) => defineChannel(c, `states[${i}]`))];
  const stateIds = new Set(bandChannels.map((c) => c.id));
  const items = aligned.objects.filter((o) => !stateIds.has(o.type) && !o.hidden)
    .sort((a, b) => compareItems(a, b, registry.order));

  return {
    fps,
    doc: d,
    registry,
    anchors: aligned.rows,
    byKey: aligned.byKey,
    objects: aligned.objects,
    items,
    states: buildStates(channels, { anchors: aligned.rows, objects: aligned.objects, ctx }),
    cues: buildCues(items, registry),
  };
}

/**
 * **人が触れる帯**（タイムラインのレーンに出るのはこれ）。
 * 状態（`layer: 'state'`。チェーン・処理中など）は帯として置かないので外す ——
 * `objects` は整列の結果そのもので状態のものも含んでいるため、レーンがそれを直接読むと消えない。
 */
export function editableObjects(timeline) {
  const stateTypes = new Set(Object.values(timeline?.states ?? {})
    .filter((e) => e.channel?.kind === 'bands')
    .map((e) => e.channel.id));
  return (timeline?.objects ?? []).filter((o) => !stateTypes.has(o.type));
}
